/**
 * @typedef {Object} OutreachLandingPageConfig
 * @property {1} schemaVersion
 * @property {string} variantId
 * @property {string} route
 * @property {string} angleName
 * @property {string} copyRevision
 * @property {string} messageVariant
 * @property {string} landingVariant
 * @property {string} senderVariant
 * @property {string} eyebrow
 * @property {string} headline
 * @property {string} subheadline
 * @property {readonly string[]} benefits
 * @property {string} explanationHeading
 * @property {string} explanationBody
 * @property {string} calculatorEyebrow
 * @property {string} calculatorHeading
 * @property {string} calculatorIntro
 * @property {'time_value'|'strategic_coverage'} calculatorMode
 * @property {string} comparisonEyebrow
 * @property {readonly {heading: string, body: string}[]} comparisonCards
 * @property {string} comparisonSupportingText
 * @property {string} heroExample
 * @property {string} securityHeading
 * @property {string} finalCtaEyebrow
 * @property {string} finalCtaHeading
 * @property {string} ctaLabel
 * @property {string} supportingCtaText
 * @property {string} pageTitle
 * @property {string} pageDescription
 * @property {string} productVisual
 */

const REQUIRED_TEXT_FIELDS = [
  'variantId',
  'route',
  'angleName',
  'copyRevision',
  'messageVariant',
  'landingVariant',
  'senderVariant',
  'eyebrow',
  'headline',
  'subheadline',
  'explanationHeading',
  'explanationBody',
  'calculatorEyebrow',
  'calculatorHeading',
  'calculatorIntro',
  'calculatorMode',
  'comparisonEyebrow',
  'securityHeading',
  'finalCtaEyebrow',
  'finalCtaHeading',
  'ctaLabel',
  'pageTitle',
  'pageDescription',
  'productVisual',
];

const MACHINE_VALUE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,79}$/;

function defineOutreachLandingPage(config) {
  for (const field of REQUIRED_TEXT_FIELDS) {
    if (typeof config[field] !== 'string' || !config[field].trim()) {
      throw new Error(`Invalid outreach landing-page field: ${field}`);
    }
  }
  for (const field of [
    'variantId',
    'copyRevision',
    'messageVariant',
    'landingVariant',
    'senderVariant',
  ]) {
    if (!MACHINE_VALUE.test(config[field])) {
      throw new Error(`Invalid outreach landing-page identifier: ${field}`);
    }
  }
  if (
    config.schemaVersion !== 1 ||
    !config.route.startsWith('/try/') ||
    config.benefits?.length !== 3 ||
    config.comparisonCards?.length !== 2 ||
    !['time_value', 'strategic_coverage'].includes(config.calculatorMode)
  ) {
    throw new Error(`Invalid outreach landing-page configuration: ${config.variantId}`);
  }
  for (const card of config.comparisonCards) {
    if (!card?.heading?.trim() || !card?.body?.trim()) {
      throw new Error(`Invalid outreach landing-page comparison: ${config.variantId}`);
    }
  }
  return Object.freeze({
    ...config,
    benefits: Object.freeze([...config.benefits]),
    comparisonCards: Object.freeze(
      config.comparisonCards.map((card) => Object.freeze({ ...card })),
    ),
  });
}

const TEAM_INTELLIGENCE_V1_1 = defineOutreachLandingPage({
  schemaVersion: 1,
  variantId: 'team_intelligence_v1',
  route: '/try/team-intelligence',
  angleName: 'Team intelligence',
  copyRevision: 'team_intelligence_v1.1',
  messageVariant: 'team_intelligence_v1',
  landingVariant: 'team_intelligence_v1',
  senderVariant: 'mikael_tryaffinityai_app',
  eyebrow: 'For founders and leadership teams',
  headline: 'Your leadership team is smarter than a single AI prompt.',
  subheadline:
    'AffinityAI combines independent perspectives from your team with AI to turn one strategic uncertainty into three plausible scenarios and one shared map—before the meeting.',
  benefits: [
    'Start with the uncertainty actually facing your company.',
    'Let each leader contribute independently.',
    'See signals, actions and critical unknowns in one shared map.',
  ],
  explanationHeading: 'Better strategy needs more than one perspective',
  explanationBody:
    'Most AI strategy work starts and ends with one person. AffinityAI gives your leadership team room to think independently before AI synthesizes the patterns. You enter the discussion with differences visible, assumptions surfaced and a shared starting point.',
  calculatorEyebrow: 'A practical business case',
  calculatorHeading: 'More perspectives without adding another strategy workshop',
  calculatorIntro:
    'Estimate the leadership time already invested in recurring preparation—and the annual value a lighter, shared scenario rhythm could help protect.',
  calculatorMode: 'time_value',
  comparisonEyebrow: 'Beyond ordinary AI chat',
  comparisonCards: [
    {
      heading: 'Ordinary AI chat',
      body: 'Often reflects one person’s framing and ends with one generated answer.',
    },
    {
      heading: 'AffinityAI',
      body: 'Structures several futures, keeps contributions independent and helps the team see where judgment differs.',
    },
  ],
  comparisonSupportingText: '',
  heroExample: '',
  securityHeading: 'Built for confidential strategic work',
  finalCtaEyebrow: 'Begin with one uncertainty',
  finalCtaHeading: 'Give your team a stronger starting point for the next strategic discussion.',
  ctaLabel: 'Create a free scenario map',
  supportingCtaText:
    'Start with one real uncertainty. Invite your team when the first map is ready.',
  pageTitle: 'Turn Leadership Perspectives Into a Shared Scenario Map | AffinityAI',
  pageDescription:
    'Combine independent leadership-team perspectives with AI to explore one uncertainty through three plausible scenarios and a shared map.',
  productVisual: 'affinityai-collaborative-workflow',
});

const STRATEGY_RHYTHM_V1_1 = defineOutreachLandingPage({
  schemaVersion: 1,
  variantId: 'strategy_rhythm_v1',
  route: '/try/strategy-rhythm',
  angleName: 'Strategy rhythm',
  copyRevision: 'strategy_rhythm_v1.1',
  messageVariant: 'strategy_rhythm_v1',
  landingVariant: 'strategy_rhythm_v1',
  senderVariant: 'miksu_tryaffinityai_com',
  eyebrow: 'A lighter strategy rhythm for growing companies',
  headline: 'Keep strategy moving between meetings.',
  subheadline:
    'Turn one strategic uncertainty into a structured scenario map in five minutes, invite your team, and build on the picture as conditions change.',
  benefits: [
    'No workshop preparation or blank presentation required.',
    'Use one repeatable structure for signals, actions and unknowns.',
    'Build on what your team has already learned.',
  ],
  explanationHeading: 'Strategy without another strategy project',
  explanationBody:
    'Strategy work should not require launching another internal project. Start with the uncertainty already on your mind. AffinityAI structures the possibilities, prepares the first map and makes it easy to bring in your team—so strategic thinking becomes an ongoing rhythm rather than an occasional exercise.',
  calculatorEyebrow: 'A practical business case',
  calculatorHeading: 'What could a lighter strategy rhythm be worth?',
  calculatorIntro:
    'Estimate annual time value tied up in recurring strategy preparation, then compare it with the current annual Company plan.',
  calculatorMode: 'time_value',
  comparisonEyebrow: 'Beyond ordinary AI chat',
  comparisonCards: [
    {
      heading: 'Ordinary AI chat',
      body: 'Often reflects one person’s framing and ends with one generated answer.',
    },
    {
      heading: 'AffinityAI',
      body: 'Structures several futures, keeps contributions independent and helps the team see where judgment differs.',
    },
  ],
  comparisonSupportingText: '',
  heroExample: '',
  securityHeading: 'Built for confidential strategic work',
  finalCtaEyebrow: 'Begin with one uncertainty',
  finalCtaHeading: 'Give your team a stronger starting point for the next strategic discussion.',
  ctaLabel: 'Create a free scenario map',
  supportingCtaText:
    'The first map is a starting point. Continue it as facts and perspectives change.',
  pageTitle: 'Keep Strategy Moving Between Meetings | AffinityAI',
  pageDescription:
    'Create a scenario map in five minutes, invite your team and continue the same strategic work as conditions change.',
  productVisual: 'affinityai-scenario-continuity',
});

const STRATEGY_RHYTHM_V1_2 = defineOutreachLandingPage({
  schemaVersion: 1,
  variantId: 'strategy_rhythm_v1',
  route: '/try/strategy-rhythm',
  angleName: 'Strategy rhythm',
  copyRevision: 'strategy_rhythm_v1.2',
  messageVariant: 'strategy_rhythm_v1',
  landingVariant: 'strategy_rhythm_v1',
  senderVariant: 'miksu_tryaffinityai_com',
  eyebrow: 'Strategy without the strategy project',
  headline: 'Make strategic thinking a lightweight team habit.',
  subheadline:
    'Start with one uncertainty. In five minutes, AffinityAI turns it into three plausible scenarios your team can explore independently—so you notice risks, options and disagreements before they become urgent.',
  benefits: [
    'Explore one real question—not your company’s entire strategy.',
    'Let everyone contribute independently through a short, focused exercise.',
    'See unexpected angles and differences in one shared map.',
  ],
  explanationHeading: 'Serious insight without a heavyweight process',
  explanationBody:
    'Strategy work often waits for the next offsite, facilitator or presentation. AffinityAI lets the team explore one live uncertainty at a time—before circumstances force the discussion.',
  calculatorEyebrow: 'A practical business case',
  calculatorHeading: 'What would one strategic question a month create?',
  calculatorIntro:
    'AffinityAI does not require a large strategy initiative. See what happens when your team explores just one live uncertainty at a time.',
  calculatorMode: 'strategic_coverage',
  comparisonEyebrow: 'A lighter way to work strategically',
  comparisonCards: [
    {
      heading: 'Traditional strategy project',
      body: 'Often requires an offsite, a facilitator, a presentation and enough free time from the whole leadership team.',
    },
    {
      heading: 'AffinityAI',
      body: 'Start with one current question. Each person contributes independently, and AI turns the different perspectives into one shared map.',
    },
  ],
  comparisonSupportingText:
    'You do not need to reveal every company secret or define the entire strategy. Begin with the question already occupying your mind.',
  heroExample:
    'Start with a question like: What if our largest customer slows down? Should we enter a new market? Which bet deserves the next six months?',
  securityHeading: 'Private enough for real questions. Light enough to use often.',
  finalCtaEyebrow: 'Start smaller than a strategy project',
  finalCtaHeading: 'Try AffinityAI with one uncertainty you are already thinking about.',
  ctaLabel: 'Create a free scenario map',
  supportingCtaText: 'No workshop, facilitator or strategy deck required.',
  pageTitle: 'Make Strategic Thinking a Lightweight Team Habit | AffinityAI',
  pageDescription:
    'Explore one live uncertainty through three plausible scenarios and make collaborative strategic thinking a lightweight team habit.',
  productVisual: 'affinityai-scenario-continuity',
});

/** Immutable attribution history, including revisions that are no longer active. */
export const OUTREACH_LANDING_PAGE_REVISIONS = Object.freeze({
  'team_intelligence_v1.1': TEAM_INTELLIGENCE_V1_1,
  'strategy_rhythm_v1.1': STRATEGY_RHYTHM_V1_1,
  'strategy_rhythm_v1.2': STRATEGY_RHYTHM_V1_2,
});

/** @type {Readonly<Record<string, OutreachLandingPageConfig>>} */
export const OUTREACH_LANDING_PAGES = Object.freeze({
  team_intelligence_v1: TEAM_INTELLIGENCE_V1_1,
  strategy_rhythm_v1: STRATEGY_RHYTHM_V1_2,
});

export const OUTREACH_LANDING_PAGE_ROUTES = Object.freeze(
  Object.values(OUTREACH_LANDING_PAGES).reduce((routes, config) => {
    routes[config.route] = config;
    return routes;
  }, {}),
);

const ADDITIONAL_OUTREACH_ANALYTICS = Object.freeze({
  '/try/before-everyone-agrees': Object.freeze({
    landingVariant: 'before_everyone_agrees_v1',
    copyRevision: 'before_everyone_agrees_v1.0',
  }),
});

export function getOutreachLandingPageById(variantId) {
  return OUTREACH_LANDING_PAGES[String(variantId || '')] || null;
}

export function getOutreachLandingPageByRoute(route) {
  return OUTREACH_LANDING_PAGE_ROUTES[String(route || '')] || null;
}

export function getOutreachAnalyticsDimensions(route) {
  const config = getOutreachLandingPageByRoute(route);
  if (!config) return ADDITIONAL_OUTREACH_ANALYTICS[String(route || '')] || null;
  return {
    messageVariant: config.messageVariant,
    landingVariant: config.landingVariant,
    copyRevision: config.copyRevision,
    senderVariant: config.senderVariant,
  };
}

export function getOutreachCalculatorMode(route) {
  return getOutreachLandingPageByRoute(route)?.calculatorMode || null;
}
